'use client';

import dynamic from 'next/dynamic';
import React, { useState, useCallback } from 'react';
import useSWR from 'swr';
import { CardHeader } from '@core/design-system/card/app';
import { BenchmarkApp as Benchmark } from '@core/design-system/chart/components/benchmarks/app';
import { microservicesApi } from '@core/market-data-sdk';
import { createSuccessRateSeries } from '@core/design-system/chart/create-series';
import { CHART_OPTIONS } from '@mais-retorno-front/libs/util-helpers';
import { AlertCircle } from 'lucide-react';
import Loading from './loading';

const BaseChart = dynamic(
  () => import('@core/design-system/chart/components/base-chart'),
  {
    ssr: false,
    loading: () => <Loading />,
  }
) as any;

interface Props {
  identifier: string;
}

// Função fetcher para o SWR
const fetchSuccessRate = async (identifier: string) => {
  const data = await microservicesApi.windows.getSuccessRate({ identifier });
  return createSuccessRateSeries({
    response: data,
    identifier,
  });
};

export default function SuccessRateCard({ identifier }: Props) {
  const [benchmarkSeries, setBenchmarkSeries] = useState<any[]>([]);
  const [isLoadingBenchmarks, setIsLoadingBenchmarks] = useState(false);

  // Buscar dados iniciais usando SWR
  const {
    data: initialSeries,
    error,
    isLoading,
  } = useSWR(
    identifier ? `success-rate-${identifier}` : null,
    () => fetchSuccessRate(identifier),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const handleBenchmarksChange = useCallback(
    async (selected: string | string[]) => {
      const benchmarks = Array.isArray(selected) ? selected : [selected];

      if (!benchmarks.length) {
        setBenchmarkSeries([]);
        return;
      }

      setIsLoadingBenchmarks(true);
      try {
        const newBenchmarkSeries = await Promise.all(
          benchmarks.map(async (benchmark) => {
            const data = await microservicesApi.windows.getSuccessRate({
              identifier,
              benchmark,
            });

            return createSuccessRateSeries({
              response: data,
              benchmark,
            });
          })
        );

        setBenchmarkSeries(newBenchmarkSeries);
      } catch (err) {
        console.error('Erro ao buscar benchmarks da taxa de sucesso', err);
      } finally {
        setIsLoadingBenchmarks(false);
      }
    },
    [identifier]
  );

  // Mostrar erro se houver
  if (error) {
    return (
      <CardHeader title="Taxa de Sucesso">
        <div className="text-center space-y-4 py-8">
          <div className="flex items-center justify-center">
            <AlertCircle
              className="w-5 h-5 mr-2 text-red-500"
              aria-hidden="true"
            />
            <span className="text-gray-700">
              Não foi possível carregar os dados da taxa de sucesso.{' '}
            </span>
            <br />
            <span className="text-gray-700">{error.message}</span>
          </div>
        </div>
      </CardHeader>
    );
  }

  // Combinar série inicial com benchmarks
  const series = initialSeries ? [initialSeries, ...benchmarkSeries] : [];

  return (
    <CardHeader
      title="Taxa de Sucesso"
      subTitle="% das vezes em que este fundo tem uma rentabilidade maior que um indicador em uma janela móvel"
      headerActions={
        <Benchmark
          onSelectedBenchmarksChange={handleBenchmarksChange}
          userIsPrime={true}
        />
      }
    >
      <BaseChart
        hasWatermark={true}
        series={series}
        chartOptions={CHART_OPTIONS.successRateChatOptions}
        loading={isLoading || isLoadingBenchmarks}
        className="min-h-[260px]"
      />
    </CardHeader>
  );
}
